import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import { useUser } from '~/common/hooks/useAuthentication';
import { useFloatingMenu } from '~/common/hooks/useFloatingMenu';
import { useNavFeatureFlags } from '~/common/hooks/useNavFeatureFlags';
import { useProductAndCategories } from '~/common/hooks/useProductAndCategories';
import useUrl from '~/common/hooks/useUrl';
import Portal from '~/common/mui/Portal/Portal';
import { hexToRgba } from '~/common/util/format';
import { CATEGORY_DETAILS, CATEGORY_IDS } from '~/pages/Home/constants';
import palette from '~/theme/mui/palette';

import { HomeActions, HomeThunks } from '../../../pages/Home/redux';

type MenuItem = {
  label: string;
  categoryId?: number; // Only for boltItems
  to?: string; // Only for infoItems
  flagName?: string;
};

type FloatingMenuProps = {
  boltItems: MenuItem[];
  infoItems: MenuItem[];
  position: { x: number; y: number };
  onClose: () => void;
};
type RowProps = {
  title: string;
  items: MenuItem[];
  onItemSelect: (item: MenuItem) => void;
};

type FloatingMenuContainerProps = {
  allCategories: any[];
};

const RenderRow = memo(function RenderRow({
  title,
  items,
  onItemSelect,
  selectedCategory,
}: Readonly<RowProps & { selectedCategory: number | undefined }>) {
  const rowStyle = {
    textAlign: 'left' as const,
    borderRadius: 8,
    width: '100%',
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
  };

  return (
    <Box component="section" style={rowStyle}>
      <Box
        component="h2"
        style={{
          fontWeight: 600,
          fontSize: '0.75rem',
          color: palette.gray[600],
          marginRight: 12,
          whiteSpace: 'nowrap',
          borderRight: `1px solid ${palette.gray[600]}`,
          paddingRight: 12,
        }}
      >
        {title}
      </Box>
      <Box
        component="section"
        style={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          component="nav"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {items.map((item) => {
            const currentPath = globalThis.location.pathname;
            const isOnSummaryRoute = currentPath.includes('/summary/');
            let isActive = false;
            if (item.categoryId !== undefined) {
              // For boltItems, only show as active if we're NOT on a summary route AND selectedCategory matches
              isActive =
                !isOnSummaryRoute && selectedCategory === item.categoryId;
            } else if (item.to) {
              // For infoItems, only show as active if we're on a summary route AND path matches
              isActive = isOnSummaryRoute && currentPath === item.to;
            }
            // If the item is a loading indicator, disable click
            const isLoading = item.label === 'Loading...';
            return (
              <Button
                key={item.label}
                style={{
                  minHeight: '2.5rem',
                  minWidth: '5rem',
                  maxWidth: '8rem',
                  padding: '4px 12px',
                  cursor: isActive || isLoading ? 'default' : 'pointer',
                  border: 'none',
                  background: isActive ? palette.blue[600] : 'transparent',
                  color: isActive ? palette.white : palette.gray[900],
                  borderRadius: 8,
                  transition: 'background 0.2s',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 1,
                }}
                title={item.label}
                onMouseEnter={(e) => {
                  if (!isActive && !isLoading) {
                    e.currentTarget.style.background = palette.blue[50];
                    e.currentTarget.style.color = palette.gray[900];
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive && !isLoading) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = palette.gray[900];
                  }
                }}
                onClick={() => onItemSelect(item)}
                disabled={isActive || isLoading}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {item.label}
                </span>
              </Button>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
});

function FloatingMenu({
  boltItems,
  infoItems,
  position,
  onClose,
}: Readonly<FloatingMenuProps>) {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { isUnderwriter } = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedCategory = useSelector((state) => state.home?.selectedCategory);
  const categories = useSelector(
    (state) => state.home?.navigationItems?.categories,
  );

  useEffect(() => {
    if (!categories || categories.length === 0) {
      dispatch(HomeThunks.getNavigationDetails(false, isUnderwriter));
    }
    // No need to clear selectedCategory - route-based logic handles active states
  }, [dispatch, categories, isUnderwriter]);

  const shouldNavigateToBolt = useCallback((boltPath: string): boolean => {
    const isOnSummaryPage = globalThis.location.pathname.includes('/summary/');
    const isOnBoltRoot = globalThis.location.pathname === boltPath;
    return !isOnBoltRoot || isOnSummaryPage;
  }, []);

  const handleBoltItemSelect = useCallback(
    (item: MenuItem) => {
      const selectedCategoryDetail = CATEGORY_DETAILS.find(
        (cat) => cat.id === item.categoryId,
      );

      const categoryNavName = Object.keys(CATEGORY_IDS).find(
        (key) => CATEGORY_IDS[key] === selectedCategoryDetail.id,
      );

      dispatch(HomeActions.setSelectedAlert(null));
      dispatch(HomeActions.setClickedSelectedCategory(categoryNavName));
      dispatch(HomeActions.setSelectedCategory(selectedCategoryDetail.id));

      const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
        obfuscatedLoanIdentifier;
      const boltPath = `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}/`;

      if (shouldNavigateToBolt(boltPath)) {
        navigate(boltPath);
      }
    },
    [dispatch, navigate, obfuscatedLoanIdentifier, shouldNavigateToBolt],
  );

  const handleInfoItemSelect = useCallback(
    (item: MenuItem) => {
      // Don't clear selectedCategory - preserve it for when user returns to BOLT
      // The route-based logic in RenderRow will handle showing correct active state
      dispatch(HomeActions.setClickedSelectedCategory(undefined));
      dispatch(HomeActions.setSelectedAlert(null));
      navigate(item.to);
    },
    [dispatch, navigate],
  );

  const handleSelect = useCallback(
    (item: MenuItem) => {
      if (item.categoryId !== undefined) {
        handleBoltItemSelect(item);
      } else if (item.to) {
        handleInfoItemSelect(item);
      }
      onClose();
    },
    [handleBoltItemSelect, handleInfoItemSelect, onClose],
  );

  return (
    <Portal>
      <Box
        component="section"
        sx={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          transform: 'translate(-50%, -50%)',
          zIndex: 2000,
          background: palette.white,
          boxShadow: `0 2px 12px ${hexToRgba(palette.black, 0.2)}`,
          borderRadius: 8,
          padding: '1rem',
          minWidth: 320,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 'fit-content',
          gap: 2,
        }}
        tabIndex={-1}
        onBlur={(e) => {
          // Only close if focus moved outside the nav entirely
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            onClose();
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Floating Menu System"
      >
        <RenderRow
          title="BOLT"
          items={boltItems}
          onItemSelect={handleSelect}
          selectedCategory={selectedCategory}
        />
        <RenderRow
          title="1003"
          items={infoItems}
          onItemSelect={handleSelect}
          selectedCategory={selectedCategory}
        />
      </Box>
    </Portal>
  );
}

export const FloatingMenuContainer = React.memo(function FloatingMenuContainer({
  allCategories,
}: FloatingMenuContainerProps) {
  const { obfuscatedLoanIdentifier, isReadOnly } = useUrl();
  const { menu, closeMenu } = useFloatingMenu();
  const { filteredCategories } = useProductAndCategories();
  const featureFlags = useNavFeatureFlags();

  // Use the same logic as CategoriesNav for boltItems
  const boltItems = useMemo(() => {
    if (!Array.isArray(allCategories) || allCategories.length === 0)
      return [
        {
          label: 'Loading...',
          categoryId: undefined,
          flagName: undefined,
          to: undefined,
          disabled: false,
        },
      ];
    return allCategories
      .map((cat) => {
        const detail = CATEGORY_DETAILS.find(
          (d) => d.id === cat.navigationCategoryId,
        );
        if (!detail) return null;
        return {
          label: detail.name,
          categoryId: detail.id,
        };
      })
      .filter(Boolean);
  }, [allCategories]);

  const infoItems = useMemo(
    () =>
      filteredCategories
        .filter(({ flagName }) => featureFlags[flagName])
        .map(({ label }) => {
          const lowerLabel = label.toLowerCase();
          return {
            key: lowerLabel,
            label,
            to: `/${obfuscatedLoanIdentifier.obfuscatedLenderDatabaseId}/${obfuscatedLoanIdentifier.obfuscatedLoanRecordId}/summary/${lowerLabel}`,
          };
        }),
    [obfuscatedLoanIdentifier, filteredCategories, featureFlags],
  );

  if (!menu.visible || isReadOnly) return null;

  return (
    <FloatingMenu
      boltItems={boltItems}
      infoItems={infoItems}
      position={menu.position}
      onClose={closeMenu}
    />
  );
});
